<script setup lang="ts">
import type { VInputNumberProps } from "./VInputNumber.types";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import VIcon from "../Icon/VIcon.vue";
import { useFieldId } from "../useFieldId";
import {
  affixCva,
  clearButtonCva,
  containerCva,
  errorMessageCva,
  inputContainerCva,
  inputCva,
  labelCva,
  requiredCva,
  stepButtonCva,
} from "./VInputNumber.cva";

const {
  label = "",
  icon = undefined,
  disabled = false,
  hasPerms = true,
  disabledReason = undefined,
  layout = "is-column",
  size = "medium",
  min = 0,
  max = 0,
  step = 1,
  decimals = null,
  stepper = true,
  invalid = false,
  required = false,
  clearable = false,
  placeholder = "",
  prefix = "",
  unit = "",
  errorMessage = "",
  ariaDescribedby = undefined,
  help = undefined,
  id: rawId = undefined,
  ariaLabelledby = undefined,
} = defineProps<VInputNumberProps>();

const { t, te, locale } = useI18n();
const id = useFieldId(() => rawId);

/** Built-in `zero | one | other` pipes when i18n keys are missing. */
const UNIT_PIPES: Record<string, Record<string, string>> = {
  days: {
    en: "days | day | days",
    nl: "dagen | dag | dagen",
  },
};

function pickPlural(pipe: string, count: number): string {
  const parts = pipe.split("|").map(part => part.trim()).filter(Boolean);
  if (parts.length === 0) {
    return pipe;
  }
  if (parts.length === 1) {
    return parts[0] ?? pipe;
  }
  if (parts.length === 2) {
    return count === 1 ? (parts[0] ?? pipe) : (parts[1] ?? pipe);
  }
  if (count === 0) {
    return parts[0] ?? pipe;
  }
  if (count === 1) {
    return parts[1] ?? pipe;
  }
  return parts[2] ?? pipe;
}

const modelValue = defineModel<number | null | undefined>({ default: undefined });

/** Last committed in-range value - used to restore when !clearable. */
const lastValidValue = ref<number | undefined>(undefined);

/** While the user edits the number it is shown as typed; otherwise it is padded to `decimals`. */
const focused = ref(false);

const isDisabled = computed(() => disabled || !hasPerms || Boolean(disabledReason));

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === "" || (typeof value === "number" && Number.isNaN(value));
}

/** Cuts decimals past `decimals` while typing, so "12.345" becomes 12.34 and never rounds up under the cursor. */
function truncateDecimals(value: number): number {
  if (decimals === null) {
    return value;
  }
  const text = String(value);
  // Exponent notation (very small or very large numbers) has no decimal part to cut in the string.
  if (text.includes("e")) {
    return roundDecimals(value);
  }
  const dotIndex = text.indexOf(".");
  if (dotIndex === -1) {
    return value;
  }
  return Number(text.slice(0, decimals === 0 ? dotIndex : dotIndex + decimals + 1));
}

/** Rounds to `decimals`, which also drops floating point noise from stepping (0.1 + 0.2). */
function roundDecimals(value: number): number {
  if (decimals === null) {
    return value;
  }
  return Number(value.toFixed(decimals));
}

function formatValue(value: number): string {
  if (decimals === null) {
    return String(value);
  }
  return value.toFixed(decimals);
}

function parseInputValue(raw: string | number | null | undefined): number | undefined {
  if (isEmptyValue(raw)) {
    return undefined;
  }
  const num = typeof raw === "number" ? raw : Number(raw);
  if (Number.isNaN(num)) {
    return undefined;
  }
  return truncateDecimals(num);
}

function isInRange(value: number): boolean {
  if (value < Number(min)) {
    return false;
  }
  if (max !== 0 && value > Number(max)) {
    return false;
  }
  return true;
}

function rememberValid(value: number | null | undefined) {
  if (isEmptyValue(value)) {
    return;
  }
  const num = Number(value);
  if (Number.isNaN(num) || !isInRange(num)) {
    return;
  }
  lastValidValue.value = num;
}

watch(modelValue, (value) => {
  rememberValid(value);
}, { immediate: true });

/** Empty shows blank; never coerce null/undefined → min on load. */
const inputValue = computed({
  get: () => {
    if (isEmptyValue(modelValue.value)) {
      return "";
    }
    if (decimals === null || focused.value) {
      return modelValue.value;
    }
    return formatValue(Number(modelValue.value));
  },
  set: (raw: string | number | null) => {
    modelValue.value = parseInputValue(raw);
  },
});

function defaultStepUpValue(): number {
  return Number(min) > 0 ? Number(min) : Number(step);
}

function clearModel() {
  modelValue.value = undefined;
}

function decreaseValue() {
  if (isEmptyValue(modelValue.value)) {
    return;
  }
  const current = Number(modelValue.value);
  if (current <= Number(min)) {
    if (clearable) {
      clearModel();
    }
    return;
  }
  const next = roundDecimals(current - Number(step));
  if (next < Number(min)) {
    if (clearable) {
      clearModel();
      return;
    }
    modelValue.value = Number(min);
    return;
  }
  modelValue.value = next;
}

function increaseValue() {
  if (isEmptyValue(modelValue.value)) {
    modelValue.value = roundDecimals(defaultStepUpValue());
    return;
  }
  const next = roundDecimals(Number(modelValue.value) + Number(step));
  if (max && next > max) {
    modelValue.value = Number(max);
    return;
  }
  modelValue.value = next;
}

function checkValue() {
  if (isEmptyValue(modelValue.value)) {
    if (clearable) {
      clearModel();
      return;
    }
    modelValue.value = lastValidValue.value ?? Number(min);
    return;
  }
  const value = Number(modelValue.value);
  if (Number.isNaN(value)) {
    if (clearable) {
      clearModel();
      return;
    }
    modelValue.value = lastValidValue.value ?? Number(min);
    return;
  }
  if (value < Number(min)) {
    modelValue.value = Number(min);
    return;
  }
  if (max !== 0 && value > Number(max)) {
    modelValue.value = Number(max);
    return;
  }
  const rounded = roundDecimals(value);
  if (rounded !== value) {
    modelValue.value = rounded;
  }
}

function onFocus() {
  focused.value = true;
}

function onBlur() {
  focused.value = false;
  checkValue();
}

const decreaseDisabled = computed(() => {
  if (isDisabled.value) {
    return true;
  }
  if (isEmptyValue(modelValue.value)) {
    return true;
  }
  // At min: allow minus to clear when clearable; otherwise disable.
  if (Number(modelValue.value) <= Number(min)) {
    return !clearable;
  }
  return false;
});

/** True when minus would clear (clearable + at/below clearable threshold). */
const willClearOnDecrease = computed(() => {
  if (!clearable || isEmptyValue(modelValue.value)) {
    return false;
  }
  const current = Number(modelValue.value);
  if (current <= Number(min)) {
    return true;
  }
  return current - Number(step) < Number(min);
});

const decreaseIcon = computed(() => (
  willClearOnDecrease.value ? "x" as const : "minus" as const
));

const increaseDisabled = computed(() => {
  if (isDisabled.value) {
    return true;
  }
  if (isEmptyValue(modelValue.value)) {
    return false;
  }
  if (!max) {
    return false;
  }
  return Number(modelValue.value) >= Number(max);
});

/** The minus button already clears at the bottom of the range, so the clear button would only repeat it there. */
const showClearButton = computed(() => {
  if (!clearable || isDisabled.value || isEmptyValue(modelValue.value)) {
    return false;
  }
  return !(stepper && willClearOnDecrease.value);
});

const errorId = computed(() => `${id.value}-error`);

const computedAriaInvalid = computed(() => {
  if (invalid || errorMessage) {
    return "true";
  }
  return undefined;
});

const computedAriaDescribedby = computed(() => {
  const parts: string[] = [];
  if (ariaDescribedby) {
    parts.push(ariaDescribedby);
  }
  if (errorMessage) {
    parts.push(errorId.value);
  }
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(" ");
});

const unitLabel = computed(() => {
  if (!unit) {
    return "";
  }
  const count = isEmptyValue(modelValue.value) ? 0 : Math.abs(Number(modelValue.value));
  if (unit.includes("|")) {
    return pickPlural(unit, count);
  }
  const key = `base.inputNumber.units.${unit}`;
  if (te(key)) {
    return String(t(key, count));
  }
  const localeKey = String(locale.value).slice(0, 2);
  const builtin = UNIT_PIPES[unit]?.[localeKey] ?? UNIT_PIPES[unit]?.en;
  if (builtin) {
    return pickPlural(builtin, count);
  }
  return unit;
});

/** The number with its prefix and unit ("€ 12.50", "30 days"), so a screen reader hears what a sighted user reads. */
const valueText = computed(() => {
  if ((!unitLabel.value && !prefix) || isEmptyValue(modelValue.value)) {
    return undefined;
  }
  return [prefix, formatValue(Number(modelValue.value)), unitLabel.value].filter(Boolean).join(" ");
});

const decreaseLabel = computed(() => (willClearOnDecrease.value ? t("base.clear") : t("base.decrease")));

const inputMode = computed(() => (decimals !== null && decimals > 0 ? "decimal" : "numeric"));

const inputAlign = computed(() => {
  if (!stepper) {
    return "right" as const;
  }
  if (unitLabel.value && !prefix) {
    return "end" as const;
  }
  if (prefix && !unitLabel.value) {
    return "start" as const;
  }
  return "center" as const;
});

/** Without a stepper the last element before the edge keeps VInput's `pr-5`, or a narrower gap before the clear button. */
const endPadding = computed(() => (showClearButton.value ? "pr-2" : "pr-5"));

const classes = computed(() => {
  return {
    container: containerCva({ layout }),
    label: [labelCva({ layout }), layout === "is-row" ? "mb-0 shrink-0" : ""],
    required: requiredCva(),
    decreaseButton: stepButtonCva({
      size,
      disabled: decreaseDisabled.value,
    }),
    increaseButton: stepButtonCva({
      size,
      disabled: increaseDisabled.value,
    }),
    clearButton: clearButtonCva({ stepper }),
    inputContainer: inputContainerCva({
      size,
      stepper,
      disabled: isDisabled.value,
      invalid: invalid || Boolean(errorMessage),
    }),
    input: [
      inputCva({
        size,
        disabled: isDisabled.value,
        align: inputAlign.value,
        padStart: !stepper && !prefix,
      }),
      !stepper && !unitLabel.value ? endPadding.value : "",
    ],
    prefix: affixCva({ size, placement: stepper ? "prefix-stepper" : "prefix" }),
    unit: [
      affixCva({ size, placement: stepper ? "unit-stepper" : "unit" }),
      stepper ? "" : endPadding.value,
    ],
    errorMessage: errorMessageCva(),
  };
});
</script>

<template>
  <div :class="classes.container">
    <!-- The help question mark sits next to the label, not inside it: a button may not live in a <label>. -->
    <div v-if="label && help" class="flex items-center" :class="[classes.label]">
      <label class="inline-flex items-center gap-[5px]" :for="id">
        <VIcon
          v-if="icon"
          :name="icon"
          class="text-xs text-gray-400 dark:text-gray-500"
          aria-hidden="true"
        />
        {{ label }}
        <span v-if="required" :class="classes.required">*</span>
      </label>
      <VHelpTooltip :content="help" :subject="label" />
    </div>
    <label
      v-else-if="label"
      :class="classes.label"
      :for="id"
    >
      <VIcon
        v-if="icon"
        :name="icon"
        class="text-xs text-gray-400 dark:text-gray-500"
        aria-hidden="true"
      />
      {{ label }}
      <span v-if="required" :class="classes.required">*</span>
    </label>
    <div class="flex w-full items-center gap-2">
      <ACTooltip
        type="edit_field"
        :allow-change="hasPerms"
        :disabled-reason="disabledReason"
        class="min-w-0 flex-1"
      >
        <div :class="classes.inputContainer">
          <button
            v-if="stepper"
            :class="classes.decreaseButton"
            :disabled="decreaseDisabled"
            :aria-label="decreaseLabel"
            type="button"
            @click="decreaseValue"
          >
            <VIcon :name="decreaseIcon" class="text-xs" aria-hidden="true" />
          </button>
          <span
            v-if="prefix"
            :class="classes.prefix"
            aria-hidden="true"
            data-test="input-number-prefix"
          >
            {{ prefix }}
          </span>
          <input
            :id="id"
            v-model="inputValue"
            type="number"
            :inputmode="inputMode"
            :class="classes.input"
            :aria-invalid="computedAriaInvalid"
            :aria-describedby="computedAriaDescribedby"
            :aria-labelledby="ariaLabelledby"
            :aria-valuetext="valueText"
            :disabled="isDisabled"
            :placeholder="placeholder"
            :min="clearable ? undefined : min"
            :max="max || undefined"
            :step="step"
            @focus="onFocus"
            @blur="onBlur"
          >
          <span
            v-if="unitLabel"
            :class="classes.unit"
            aria-hidden="true"
            data-test="input-number-unit"
          >
            {{ unitLabel }}
          </span>
          <button
            v-if="showClearButton"
            :class="classes.clearButton"
            :aria-label="t('base.clear')"
            type="button"
            data-test="input-number-clear"
            @click="clearModel"
          >
            <VIcon name="x" aria-hidden="true" />
          </button>
          <button
            v-if="stepper"
            :class="classes.increaseButton"
            :disabled="increaseDisabled"
            :aria-label="t('base.increase')"
            type="button"
            @click="increaseValue"
          >
            <VIcon name="plus" class="text-xs" aria-hidden="true" />
          </button>
        </div>
      </ACTooltip>
      <slot name="suffix" />
    </div>
    <div
      v-if="errorMessage"
      :id="errorId"
      :class="classes.errorMessage"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>
